/**
 * Commerce > Products. Unified Sage catalog across both brands, from the
 * product_catalog view (GET /v1/commerce/catalog/products). Read-only.
 *
 * Columns: item code, description, brand, uom, case pack, on-hand, active.
 * Filters: q, brand, active. Click a row for per-warehouse on-hand.
 */

import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import {
  catalogBrandTone,
  formatCatalogQty,
  hasCommerceProducts,
  labelCatalogBrand,
  useCatalogProducts,
  CATALOG_BRAND_OPTIONS,
  type CatalogBrand,
  type ProductFilters,
  type ProductRow,
} from '@/lib/useCatalog';

type ProductSortColumn = 'item_code' | 'description' | 'brand' | 'uom' | 'case_pack' | 'on_hand' | 'active';

const SORTABLE: ReadonlySet<ProductSortColumn> = new Set<ProductSortColumn>([
  'item_code', 'description', 'brand', 'uom', 'case_pack', 'on_hand', 'active',
]);

function parseSort(raw: string | null): { column: ProductSortColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (!SORTABLE.has(col as ProductSortColumn)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as ProductSortColumn, dir };
}

function sortValue(r: ProductRow, col: ProductSortColumn): string | number {
  switch (col) {
    case 'item_code': return r.item_code.toLowerCase();
    case 'description': return (r.description ?? '').toLowerCase();
    case 'brand': return r.brand.toLowerCase();
    case 'uom': return (r.uom ?? '').toLowerCase();
    case 'case_pack': return r.case_pack ?? -Infinity;
    case 'on_hand': return Number(r.on_hand ?? 0);
    case 'active': return r.active ? 1 : 0;
  }
}

export function CommerceCatalogProducts() {
  const { user } = useSession();
  const canRead = hasCommerceProducts(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const brand = searchParams.get('brand') ?? '';
  const active = searchParams.get('active') ?? '';

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const setStr = (k: string, v: string) => update((sp) => (v ? sp.set(k, v) : sp.delete(k)));
  const resetFilters = () => update((sp) => ['q', 'brand', 'active', 'sort'].forEach((k) => sp.delete(k)));

  const filters: ProductFilters = useMemo(
    () => ({
      q: q || undefined,
      brand: (brand || undefined) as CatalogBrand | undefined,
      active: active === '' ? undefined : active === 'true',
    }),
    [q, brand, active],
  );

  const productsQ = useCatalogProducts(filters);

  const sort = parseSort(searchParams.get('sort'));
  const toggleSort = (col: ProductSortColumn) => {
    if (!sort || sort.column !== col) update((sp) => sp.set('sort', `${col}:asc`));
    else if (sort.dir === 'asc') update((sp) => sp.set('sort', `${col}:desc`));
    else update((sp) => sp.delete('sort'));
  };
  const sortedFor = (col: ProductSortColumn): SortDir | null => (sort && sort.column === col ? sort.dir : null);

  if (user && !canRead) return <AccessDeniedCard />;

  const rows = sort
    ? [...productsQ.rows].sort((a, b) => {
        const av = sortValue(a, sort.column);
        const bv = sortValue(b, sort.column);
        const f = sort.dir === 'asc' ? 1 : -1;
        if (av < bv) return -1 * f;
        if (av > bv) return 1 * f;
        return 0;
      })
    : productsQ.rows;

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="q" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="q"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="Item code or description..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <label htmlFor="brand" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Brand</label>
        <select
          id="brand"
          value={brand}
          onChange={(e) => setStr('brand', e.target.value)}
          className="min-w-[150px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {CATALOG_BRAND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="active" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Active</label>
        <select
          id="active"
          value={active}
          onChange={(e) => setStr('active', e.target.value)}
          className="min-w-[110px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>
      {(q || brand || active) && (
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Commerce</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Products</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Sage catalog across both brands, with total on-hand.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="products" toolName="Products" />
        </div>
      </div>

      <DataGridShell
        total={productsQ.totalCount}
        loaded={rows.length}
        countNoun="products"
        toolbar={filterBar}
        onReachEnd={productsQ.fetchNextPage}
        hasMore={productsQ.hasNextPage}
        isLoadingMore={productsQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader label="Item Code" sorted={sortedFor('item_code')} onToggle={() => toggleSort('item_code')} thClassName="px-3 py-2 w-[140px]" />
              <SortableHeader label="Description" sorted={sortedFor('description')} onToggle={() => toggleSort('description')} thClassName="px-3 py-2" />
              <SortableHeader label="Brand" sorted={sortedFor('brand')} onToggle={() => toggleSort('brand')} thClassName="px-3 py-2 w-[130px]" />
              <SortableHeader label="UOM" sorted={sortedFor('uom')} onToggle={() => toggleSort('uom')} thClassName="px-3 py-2 w-[80px]" />
              <SortableHeader label="Case Pack" sorted={sortedFor('case_pack')} onToggle={() => toggleSort('case_pack')} align="right" thClassName="px-3 py-2 w-[100px]" />
              <SortableHeader label="On Hand" sorted={sortedFor('on_hand')} onToggle={() => toggleSort('on_hand')} align="right" thClassName="px-3 py-2 w-[100px]" />
              <SortableHeader label="Active" sorted={sortedFor('active')} onToggle={() => toggleSort('active')} thClassName="px-3 py-2 w-[90px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {productsQ.isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading products...</td></tr>
            ) : productsQ.error ? (
              <tr><td colSpan={7} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {productsQ.error.message}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No products match this filter.</td></tr>
            ) : (
              rows.map((row) => <ProductListRow key={`${row.brand}:${row.item_code}`} row={row} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function ProductListRow({ row }: { row: ProductRow }) {
  const detailHref = {
    pathname: `/commerce/products/${encodeURIComponent(row.item_code.trim())}`,
    search: typeof window !== 'undefined' ? window.location.search : '',
  };
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
        <Link to={detailHref} className="hover:underline">{row.item_code}</Link>
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{row.description ?? '--'}</td>
      <td className="px-3 py-2"><Pill tone={catalogBrandTone(row.brand)}>{labelCatalogBrand(row.brand)}</Pill></td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{row.uom ?? '--'}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{row.case_pack ?? '--'}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(row.on_hand)}</td>
      <td className="px-3 py-2">
        {row.active
          ? <Pill tone="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Active</Pill>
          : <Pill tone="bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300">Inactive</Pill>}
      </td>
    </tr>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Products is gated by the commerce:read capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
